import AIWhatsappTemplateGenerator from "@components/AI-whatsapp-template-generator";
import AccordionCustom from "@components/Accordion/AccordionCustom";
import BenefitsPanel, { BenefitsCardContentProps } from "@components/BenefitsPanel";
import BlogOrCaseStudy, { BlogOrCaseStudyContent } from "@components/BlogsOrCaseStudy";
import NewLayout from "@components/new-ui/Layout";
import PageMainTitleCardV2 from "@components/PageMainTitleCardV2";
import PageSummaryV2, { SummaryItemProps } from "@components/PageSummaryV2";
import schema from "@components/faqSchema/ai-whatsapp-template-generator-faqSchema.json";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import React, { useRef } from "react";
import { BiSolidEdit } from "react-icons/bi";
import { FaChartBar } from "react-icons/fa";
import { IoMdHappy } from "react-icons/io";
import { IoCloudDoneOutline, IoMegaphoneOutline } from "react-icons/io5";
import { MdOutlineRocketLaunch } from "react-icons/md";
import { AccordionItemsProps } from "./whatsapp-conversation-pricing-calculator";
import BannerModal from "@components/BannerModal";
const scriptText = JSON.stringify(schema, null, 2);

const accordionCustomInputItems: AccordionItemsProps[] = [
    {
        question: "What can you do with a WhatsApp template generator?",
        answer: "A WhatsApp template generator lets you craft personalised message templates based on your text descriptions. Using AI, Gallabox understands the context and generates engaging messages that will help you reach business goals.",
    },
    {
        question: "How to create a WhatsApp message template for free?",
        answer: "You can create a WhatsApp message template by providing a businesses name, bio, description and selecting the tone of the message. Gallabox automatically generates the content based on the input.",
    },
    {
        question: "Why use an AI-powered WhatsApp template generator?",
        answer: (
            <>
                Business owners, marketers, sales reps, and even customer support agents can use WhatsApp Template Generator to:
                <br />
                1. Ensure consistency across conversations
                <br />
                2. Quickly answer repetitive questions
                <br />
                3. Comply with WhatsApp’s commerce policy
                <br />
                4. Personalize messages with dynamic fields
                <br />
                5. Inject automation and enable 24/7 availability.
            </>
        ),
    },
    {
        question: "How much does it cost to create templates using Gallabox's template generator?",
        answer: (
            <>
                Gallabox’s WhatsApp template generator is free to use. And there are no strings attached. Finding it hard to believe? Go on, take our{" "}
                <a className="text-blue-500 hover:text-blue-600" style={{ textDecoration: "none" }} href="#template-form">
                    template generator
                </a>{" "}
                for a spin, and you’ll see how easy it is to create high-quality branded templates.
            </>
        ),
    },
    {
        question: "Will my AI-generated WhatsApp templates be unique?",
        answer: "Although an array of factors ranging from your business’ bio to your description prompts and message type influence the end result, our AI will ensure that each message template is as unique as possible.",
    },
    {
        question: "Is Gallabox’s WhatsApp template generator easy for beginners?",
        answer: (
            <>
                Yes, Gallabox’s WhatsApp template generator is completely beginner-friendly. The intuitive interface makes it easy to input text descriptions and generate
                interactive templates. Whether you&apos;re experienced or new to{" "}
                <a
                    className="text-blue-500 hover:text-blue-600"
                    rel="noreferrer"
                    target="_blank"
                    style={{ textDecoration: "none" }}
                    href="https://gallabox.com/blog/whatsapp-business-automation"
                >
                    WhatsApp Automation
                </a>
                , you can create engaging message templates with this tool.
            </>
        ),
    },
];

const bannerContent: BlogOrCaseStudyContent[] = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/ai-whatsapp-template-generator/calci-banner-img.png",
        bannerTitle: "Estimate your WhatsApp messaging costs with our Free Conversation Pricing Calculator",
        bannerDescription:
            "Finally, here’s a straightforward and precise strategy to forecast your marketing expenses with WhatsApp Pricing Calculator. You can now prioritise your campaigns and reveal potential ways to cut costs.",
        redirectUrl: "/whatsapp-conversation-pricing-calculator",
        buttonText: "Estimate Now",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-chat-widget/og.png",
        bannerTitle: "WhatsApp Chat Widget for Engaging Leads on your Website",
        bannerDescription:
            "Generate a free WhatsApp Chat Widget for your website and landing pages in no time. Spark a conversation with your visitors right when their intent is high.",
        redirectUrl: "/whatsapp-chat-button",
        buttonText: "Generate Now",
    },
];

const BenefitsCardContent_1: BenefitsCardContentProps[] = [
    {
        icon: BiSolidEdit,
        title: "Personalize your recommendations",
        description: "Send tailored suggestions to your potential clients based on crucial factors such as their past behavior, purchase history, and much more.",
    },
    {
        icon: FaChartBar,
        title: "Empower your sales representatives",
        description: "Implement WhatsApp Flows and Chatbots to help speed up lead qualification and close deals faster.",
    },
    {
        icon: IoCloudDoneOutline,
        title: "Get a single source of truth",
        description: "Store all your customer chats in a single, organized location to ensure there are no skipped conversations or missed opportunities.",
    },
];
const BenefitsCardContent_2: BenefitsCardContentProps[] = [
    {
        icon: IoMegaphoneOutline,
        title: "Amplify your marketing efforts",
        description:
            "Gift your marketing team features such as bulk broadcasts, drip campaigns, and targeted segmentation to help them connect with the right person at the right time.",
    },
    {
        icon: IoMdHappy,
        title: "Keep customers engaged",
        description:
            "Keep your customers and prospects engaged with an automated chatbot that shares on-time updates, reminders and, responses to frequently asked questions (FAQs)",
    },
    {
        icon: MdOutlineRocketLaunch,
        title: "Drive revenue and profitability",
        description: "Create and launch Facebook and Instagram Ads that send leads straight to WhatsApp, thereby reducing your cost per lead noticeably.",
    },
];

const pageSummaryContent: SummaryItemProps[] = [
    {
        title: <h4 className="lg:text-xl text-base text-gray-600 font-medium pb-1">Manage higher ticket volume while staying lean</h4>,
        description: (
            <p className="text-base text-gray-500 pb-4">
                Don’t scale your team. Instead, scale your AI-powered{" "}
                <a href="/blog/how-to-build-a-chatgpt-based-whatsapp-ai-chatbot" className="text-blue-500 underline">
                    WhatsApp chatbot
                </a>{" "}
                workflows to answer common queries 24x7 and allow your team to focus on building relationships and closing deals.
            </p>
        ),
    },
    {
        title: <h4 className="lg:text-xl text-base text-gray-600 font-medium pb-1">Keep creativity and personalization a click away</h4>,
        description: (
            <p className="text-base text-gray-500 pb-4">
                Use Gallabox’s{" "}
                <a href="/whatsapp-ai-chatbot" className="text-blue-500 underline">
                    generative AI editor
                </a>{" "}
                to personalize your responses with just a few prompts. Responding to prospects is no longer a tedious process that takes up too much mindspace.
            </p>
        ),
    },
    {
        title: <h4 className="lg:text-xl text-base text-gray-600 font-medium pb-3">Translate prospect responses without moving out of WhatsApp</h4>,
        description:
            "With Gallabox, you don’t have to switch between tabs to translate a prospect or customer’s reply, understand the context, and paste your reply. Do it all on a single platform.",
    },
];

const WhatsappTemplateGenerator: React.FC = () => {
    const templateFormRef = useRef<HTMLDivElement>(null);
    const handleScrollToTemplateGenerator = () => {
        if (templateFormRef && templateFormRef.current) {
            templateFormRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };
    return (
        <NewLayout
            title="Free AI WhatsApp template generator: Turn Text Into Templates"
            description="Create WhatsApp message in seconds with our free AI WhatsApp Template Generator. Fast, easy & ready to use — no coding needed!"
            canonical="https://gallabox.com/ai-whatsapp-template-generator"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/ai-whatsapp-template-generator/og.png" }}
            keywords="AI WhatsApp template generator, WhatsApp template generator, WhatsApp Message template generator"
            schema={{
                heroOrOgImageAlt: "ai-whatsapp-template-generator-image",
                heroOrOgImageDescription: "Generate WhatsApp Message Templates with Gallabox AI",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/wa-business-solution/hero-image.png",
                softwareFeatureName: "ai-whatsapp-template-generator",
                faq: accordionCustomInputItems,
            }}
        >
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: scriptText }} />
            </Head>

            <BannerModal />

            <section className="lg:my-16 md:my-14 mb-8 mt-4 lg:mx-0 mx-3">
                <PageMainTitleCardV2
                    chipTitle="AI WhatsApp template generator"
                    mainTitle="Generate WhatsApp Message Templates with Gallabox AI"
                    description={
                        <p className="mb-4 text-base font-normal text-gray-600 text-center">
                            Go from text to template in seconds using Gallabox’s Free AI-powered WhatsApp Message Template Generator.{" "}
                            <a href="/blog/tips-guidelines-to-avoid-whatsapp-message-template-rejections" className="text-blue-500 underline">
                                Reduce your WhatsApp template rejections
                            </a>{" "}
                            by generating creative and compliant templates for different business use cases.
                        </p>
                    }
                    buttonText="Try now"
                    scrollHandler={handleScrollToTemplateGenerator}
                />
            </section>
            <section id="template-form" className="lg:my-20 my-10">
                <AIWhatsappTemplateGenerator focusToForm={templateFormRef} />
            </section>
            <section className="max-w-6xl mx-auto">
                <PageSummaryV2
                    mainTitle="There’s more to AI when you switch to a WhatsApp Business API-provider like Gallabox"
                    mainDescription="AI-generated WhatsApp Message templates add diligence to your template approvals and also delight prospects at scale. But we’re not even scratching the surface
                    here. WhatsApp Business APIs can remove a lot of grunt work and help you manage leads throughout their entire buyer lifecycle across deal stages."
                    imgUrl="https://assets.gallabox.com/gb-home/ai-wa-template-generator/page-summary-img.png"
                    imgAlt="AI WhatsApp template generator"
                    buttonText="Get a free consultation"
                    link="https://bit.ly/4epPjQS"
                    summaryItem={pageSummaryContent}
                />
            </section>
            <section className="lg:my-20 lg:mx-auto mx-2 my-10 flex flex-col lg:gap-8 gap-6">
                <BenefitsPanel
                    mainTitle={<h2 className="text-3xl text-gray-700 font-bold text-center">Gallabox: The All-in-One WhatsApp Automation Platform</h2>}
                    mainDescription="Whether you're a business owner, a sales head or a marketer, you can use Gallabox to turn your creative vision into reality and achieve all your business goals. See how the other features of Gallabox can help transform your marketing strategy."
                    cardContent={BenefitsCardContent_1}
                />
                <BenefitsPanel cardContent={BenefitsCardContent_2} />
            </section>
            <section className="lg:mx-auto lg:my-10 mx-2 mt-0 mb-10">
                <YellowCTAhref
                    customText={
                        <h2 className="text-2xl md:text-3xl font-bold mb-4 relative inline-block text-white">
                            Level up your marketing game with the most trusted <br /> WhatsApp Automation software
                        </h2>
                    }
                    buttonText="Start Free Trial"
                    cardStyles={{ cardColor: "#3182CE", buttonBackgroundColor: "bg-white", buttonTextColor: "text-blue-500", buttonHoverColor: "bg-gray-50" }}
                />
            </section>
            <div className="lg:max-w-6xl lg:mx-auto w-full">
                <BlogOrCaseStudy
                    mainTitle={
                        <h3 className="lg:text-3xl text-2xl font-bold text-gray-700 text-center">
                            Free Tools to <span className="text-blue-500">Grow Your Business</span> on WhatsApp
                        </h3>
                    }
                    bannerContent={bannerContent}
                />
            </div>
            <div className="lg:mt-20 mt-14 mb-10">
                <section className="max-w-6xl p-4 mx-auto">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <h2 className="lg:text-4xl text-3xl lg:text-center md:text-center text-left font-bold text-gray-700">
                            AI WhatsApp message generator: <br /> Frequently Asked Questions
                        </h2>
                    </div>
                    <AccordionCustom title="AI WhatsApp template generator" items={accordionCustomInputItems} />
                </section>
            </div>
        </NewLayout>
    );
};

export default WhatsappTemplateGenerator;
